import { Sentry } from "@/telemetry/sentry"
import { replaceDialog, resetDialogs } from "@/store/dialogs-state"
import { ConfirmDialog } from "@/components/Dialogs/ConfirmDialog"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
import {
	isBrowserStorageQuotaError,
	isNotFoundError,
	isSqliteNotADatabaseError,
	isStorageQuotaError,
} from "@/utils/error"
import { CatalogManager } from "@/lib/catalog"
import { proxy } from "comlink"
import { Primitive } from "zod"
import { closeSession } from "@/routing/project-session"
import { getSupportEmail } from "@/utils/support-email"
import { getProductName } from "@/utils/product-name"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer dialog/toast i18n
import i18n from "@/i18n"

void CatalogManager.setUserFacingErrorHandler(
	proxy((error, tags, isWarning) => {
		void handleProjectError(error, tags, isWarning)
	}),
)

async function getStorageInfoTags() {
	const estimate = await navigator.storage.estimate()
	const tags = {
		quota: estimate.quota?.toString() ?? "unknown",
		usage: estimate.usage?.toString() ?? "unknown",
		remaining:
			estimate.quota && estimate.usage
				? (estimate.quota - estimate.usage).toString()
				: "unknown",
	}
	return tags
}

const isProjectConflictError = (error: unknown) => {
	if (!(error instanceof AxiosError) || error.response?.status !== 409) {
		return false
	}

	const requestUrl = error.config?.url
	return requestUrl?.includes("/projects") ?? false
}

export async function handleProjectError(
	error: unknown,
	tags: Record<string, Primitive>,
	isWarning?: boolean,
) {
	resetDialogs()
	let logStorage = false
	let logToSentry = true

	if (
		error instanceof Error &&
		(error.name === "NotAllowedError" || error.name === "NoModificationAllowedError")
	) {
		logToSentry = false
		replaceDialog(ConfirmDialog, {
			title: i18n.t("store:handleProjectError.errorPermissionTitle"),
			message: i18n.t("store:handleProjectError.errorPermissionMessage"),
			primary: {
				title: i18n.t("store:handleProjectError.errorReload"),
				handler: () => {
					window.location.reload()
				},
			},
			canClose: true,
			trackingViewName: "project_open_permission_lost",
		})
	} else if (isStorageQuotaError(error) === true) {
		logStorage = true
		replaceDialog(ConfirmDialog, {
			title: i18n.t("store:handleProjectError.errorStorageTitle"),
			message: i18n.t("store:handleProjectError.errorStorageMessage"),
			primary: {
				title: i18n.t("store:handleProjectError.errorCloseProject"),
				handler: () => {
					void closeSession({ saveChanges: false, reason: "storage_quota_exceeded" })
				},
			},
			trackingViewName: "project_storage_quota_exceeded",
		})
	} else if (isStorageQuotaError(error) === "maybe") {
		logStorage = true
		replaceDialog(ConfirmDialog, {
			title: i18n.t("store:handleProjectError.errorMaybeStorageTitle"),
			message: i18n.t("store:handleProjectError.errorMaybeStorageMessage"),
			primary: {
				title: i18n.t("store:handleProjectError.errorCloseProject"),
				handler: () => {
					void closeSession({
						saveChanges: false,
						reason: "maybe_storage_quota_exceeded",
					})
				},
			},
			secondary: {
				title: i18n.t("store:handleProjectError.errorHaveEnoughSpace"),
				handler: async () => {
					const tags = await getStorageInfoTags()
					Sentry.captureException(
						new Error(
							"NotReadableError encountered during project update, user confirms with enough space",
						),
						{ tags },
					)
					toast.success(i18n.t("store:handleProjectError.errorReportSent"))
				},
			},
			trackingViewName: "project_maybe_storage_quota_exceeded",
		})
	} else if (isBrowserStorageQuotaError(error)) {
		logStorage = true
		const supportEmail = getSupportEmail()
		replaceDialog(ConfirmDialog, {
			title: i18n.t("store:handleProjectError.errorBrowserStorageTitle"),
			message: i18n.t("store:handleProjectError.errorBrowserStorageMessage", {
				email: supportEmail,
			}),
			primary: {
				title: i18n.t("store:handleProjectError.errorCloseProject"),
				handler: () => {
					void closeSession({
						saveChanges: false,
						reason: "browser_storage_quota_exceeded",
					})
				},
			},
			trackingViewName: "project_browser_storage_quota_exceeded",
		})
	} else if (isProjectConflictError(error)) {
		toast.error(i18n.t("store:handleProjectError.errorProjectConflict"), {
			id: "error",
			duration: 2500,
		})
	} else if (isNotFoundError(error)) {
		logToSentry = false
		toast.error(i18n.t("store:handleProjectError.errorProjectNotFound"), {
			id: "error",
			duration: 2500,
		})
	} else if (isSqliteNotADatabaseError(error)) {
		replaceDialog(ConfirmDialog, {
			title: i18n.t("store:handleProjectError.errorCatalogInvalidTitle"),
			message: i18n.t("store:handleProjectError.errorCatalogInvalidMessage", {
				productName: getProductName(),
			}),
			primary: {
				title: i18n.t("store:handleProjectError.errorCloseProject"),
				handler: () => {
					void closeSession({
						saveChanges: false,
						reason: "invalid_catalog_database",
					})
				},
			},
			canClose: true,
			trackingViewName: "project_catalog_invalid_database",
		})
	} else if (error instanceof Error && error.message.includes("WebGL")) {
		toast.error(i18n.t("store:handleProjectError.errorWebGL"), { id: "error", duration: 5000 })
	} else {
		logStorage = true
		toast.error(i18n.t("store:handleProjectError.errorGeneric"))
	}

	if (logStorage) {
		const storageTags = await getStorageInfoTags()
		tags = { ...tags, ...storageTags }
	}

	console.error("[Project Error]", error, tags)

	if (logToSentry) {
		Sentry.captureException(error, { tags, level: isWarning ? "warning" : "error" })
	}
}
